/*
  assets/gfun-manager.js — GFUN-MANAGER-RUNTIME-03

  Contract:
  - data-gfun-cost and related attributes are numeric UI contract values only.
  - Container costs may be inherited only by declared execution controls.
  - No client-side authoritative deduction or entitlement grant.
  - Unknown or unverifiable balance fails closed as unavailable, not insufficient.
*/
(function gfunManagerRuntime(window, document) {
  'use strict';

  if (!window || !document) return;
  if (window.GFunManager && window.GFunManager.__installed) return;

  var VERSION = 'v2.1.0-fail-closed-balance';
  var BOUND_ATTR = 'data-gfun-bound';

  var BALANCE_SELECTOR = [
    '[id^="GFunBalanceDisplay-"]',
    '[data-lg-gfun]',
    '[data-gcity-gfun-display]',
    '[data-gfun-display]',
    '[data-gfun-balance-display]',
    '#gfun-balance',
    '#gfun-balance-value',
    '#currentGFunBalance',
  ].join(', ');

  var COST_SOURCE_SELECTOR = [
    '[data-gfun-required]',
    '[data-gfun-cost]',
    '[data-gfun-consume]',
    '[data-gfun-consume-per-call]',
    '[data-gfun-contract-cost]',
  ].join(', ');

  var ACTION_SELECTOR = [
    '[data-gfun-action]',
    '[data-gfun-generate]',
    '[data-gcity-execute]',
    '[data-action="execute"]',
    'button[data-gfun-cost]',
    'a[data-gfun-cost]',
    'input[type="submit"][data-gfun-cost]',
    '[role="button"][data-gfun-cost]',
    'button[data-gfun-required]',
    'a[data-gfun-required]',
    'input[type="submit"][data-gfun-required]',
    '[role="button"][data-gfun-required]',
    'button[data-gfun-consume-per-call]',
    'a[data-gfun-consume-per-call]',
    'input[type="submit"][data-gfun-consume-per-call]',
    '[role="button"][data-gfun-consume-per-call]',
    'button[data-gfun-contract-cost]',
    'a[data-gfun-contract-cost]',
    'input[type="submit"][data-gfun-contract-cost]',
    '[role="button"][data-gfun-contract-cost]',
  ].join(', ');

  var HYDRATE_TARGET_SELECTOR = [
    '[data-gcity-execute]',
    '[data-gfun-generate]',
    '[data-gfun-action]',
    '[data-action="execute"]',
    '.spm-btn--submit',
    '.product-action-panel__button--primary',
    '.lg-gear__cta',
    '.lg-island-tier__button',
  ].join(', ');

  var REMAINING_PREVIEW_SELECTOR = '#remainingPreview, [data-remaining-card]';
  var BALANCE_STATUS_SELECTOR = '#balanceStatus, [data-balance-status]';
  var REMAINING_GFUN_SELECTOR = '#remainingGFun, [data-remaining-balance]';
  var SHORTAGE_AMOUNT_SELECTOR = '#shortageAmount, [data-shortage-amount]';
  var INSUFFICIENT_NOTICE_SELECTOR = '#insufficientNotice, [data-insufficient-notice]';

  var refreshPromise = null;
  var initialized = false;
  var currentBalance = 0;
  var hasKnownBalance = false;
  var currentBalanceSource = '';
  var balanceCustomerId = '';
  var activeCustomerId = null;
  var balanceGeneration = 0;
  var serverSeedAllowed = true;
  var publishedBalanceDetail = null;

  function normalizeNumber(value, fallback) {
    if (value === null || typeof value === 'undefined') {
      return typeof fallback === 'number' ? fallback : 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : typeof fallback === 'number' ? fallback : 0;
    }

    var cleaned = String(value)
      .replace(/,/g, '')
      .replace(/[^\d.-]/g, '');
    if (!cleaned) return typeof fallback === 'number' ? fallback : 0;

    var parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : typeof fallback === 'number' ? fallback : 0;
  }

  function parseBalance(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    var raw = String(value).trim().replace(/,/g, '');
    if (!/^\d+(?:\.\d+)?$/.test(raw)) return null;
    var parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  function normalizeCost(value) {
    var cost = normalizeNumber(value, 0);
    return cost < 0 ? 0 : cost;
  }

  function getCustomerData() {
    return window.CUSTOMER_DATA || window.customerData || {};
  }

  function getCustomerId() {
    var customer = getCustomerData();
    if (customer.isLoggedIn === false) return null;
    return (
      customer.id ||
      customer.customer_id ||
      customer.customerId ||
      (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) ||
      null
    );
  }

  function customerKey() {
    return String(getCustomerId() || '');
  }

  function ensureCustomer() {
    var id = customerKey();
    if (activeCustomerId === null) activeCustomerId = id;
    else if (activeCustomerId !== id) {
      activeCustomerId = id;
      balanceGeneration += 1;
      refreshPromise = null;
      serverSeedAllowed = false;
      renderUnavailableBalance('CUSTOMER_CHANGED');
    }
    return id;
  }

  function getBalanceSnapshot() {
    var id = ensureCustomer();
    var known = Boolean(id && hasKnownBalance && balanceCustomerId === id);
    return {
      customerId: id,
      known: known,
      balance: known ? currentBalance : null,
      source: known ? currentBalanceSource : '',
    };
  }

  function readLocalBalance() {
    return getBalanceSnapshot().balance;
  }

  function readFreshLocalBalance() {
    return null;
  }

  function readShopifyMetafieldBalance() {
    var customer = window.Shopify && window.Shopify.customer;
    if (!customer || String(customer.id || '') !== ensureCustomer()) return null;
    var metafields = customer.metafields;
    var custom = metafields && metafields.custom;
    var metafield = custom && custom.gfun_balance;
    var value =
      metafield &&
      typeof metafield === 'object' &&
      Object.prototype.hasOwnProperty.call(metafield, 'value')
        ? metafield.value
        : metafield;

    return parseBalance(value);
  }

  function getExecutor() {
    var executor = window.GCityExecutor;
    if (!executor) return null;
    if (executor.isReady !== true) return null;
    if (executor.__isFallbackStub === true) return null;
    if (typeof executor.execute !== 'function') return null;
    return executor;
  }

  function dispatchRuntimeEvent(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (_error) {}
  }

  function forEachNode(nodes, callback) {
    Array.prototype.forEach.call(nodes || [], callback);
  }

  function parseDisplayedBalance(raw) {
    var parsed = parseBalance(raw);
    return parsed === null ? NaN : parsed;
  }

  function seedBalanceFromDOM() {
    if (!serverSeedAllowed || !ensureCustomer()) return NaN;
    var nodes = document.querySelectorAll(BALANCE_SELECTOR + ', [data-gfun-wallet-workspace]');

    for (var index = 0; index < nodes.length; index += 1) {
      var node = nodes[index];
      if (
        !node.dataset ||
        node.dataset.gfunBalanceKnown !== 'true' ||
        node.dataset.gfunCustomerId !== ensureCustomer()
      )
        continue;
      var raw =
        node && node.dataset && (node.dataset.gfunBalance || node.dataset.balance)
          ? node.dataset.gfunBalance || node.dataset.balance
          : node
            ? node.textContent
            : '';
      var parsed = parseDisplayedBalance(raw);

      if (!Number.isNaN(parsed)) return parsed;
    }

    return NaN;
  }

  function isActionable(element) {
    return Boolean(element && element.matches && element.matches(ACTION_SELECTOR));
  }

  function findContractOwner(element) {
    if (!element || !element.closest) return null;
    return element.closest(COST_SOURCE_SELECTOR);
  }

  function getRequiredCost(element) {
    if (!element) return 0;

    var dataset = element.dataset || {};
    var own =
      dataset.gfunRequired ||
      dataset.gfunCost ||
      dataset.gfunConsumePerCall ||
      dataset.gfunConsume ||
      dataset.gfunContractCost;

    if (own !== undefined && own !== null && own !== '') {
      return normalizeCost(own);
    }

    var owner = findContractOwner(element);
    if (owner && owner !== element && owner.dataset) {
      return normalizeCost(
        owner.dataset.gfunContractCost ||
          owner.dataset.gfunRequired ||
          owner.dataset.gfunCost ||
          owner.dataset.gfunConsumePerCall ||
          owner.dataset.gfunConsume ||
          0
      );
    }

    return 0;
  }

  function findPageRequiredCost() {
    var active = document.querySelector(
      '[data-gfun-bound="true"][data-gfun-state="ready"], ' +
        '[data-gfun-bound="true"][data-gfun-state="insufficient"], ' +
        '[data-gfun-bound="true"][data-gfun-state="unavailable"]'
    );
    var node =
      active ||
      document.querySelector(ACTION_SELECTOR) ||
      document.querySelector(COST_SOURCE_SELECTOR);
    return getRequiredCost(node);
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = Boolean(hidden);
    if (hidden) element.setAttribute('hidden', 'hidden');
    else element.removeAttribute('hidden');
  }

  function updateBalanceCheckerUI(balance, unavailable) {
    var currentValue = normalizeNumber(balance, 0);
    var required = findPageRequiredCost();
    var remaining = Math.max(0, currentValue - required);
    var shortage = Math.max(0, required - currentValue);
    var authenticated = Boolean(getCustomerId());

    forEachNode(document.querySelectorAll(REMAINING_GFUN_SELECTOR), function (node) {
      node.textContent = unavailable ? '—' : String(remaining) + ' GFun';
    });

    forEachNode(document.querySelectorAll(SHORTAGE_AMOUNT_SELECTOR), function (node) {
      node.textContent = unavailable ? '—' : String(shortage);
    });

    forEachNode(document.querySelectorAll(BALANCE_STATUS_SELECTOR), function (node) {
      if (unavailable) {
        node.textContent = authenticated
          ? 'GFun balance is temporarily unavailable'
          : 'Sign in to check GFun balance';
        node.dataset.status = 'unavailable';
      } else if (!required) {
        node.textContent = 'Balance synced';
        node.dataset.status = 'ready';
      } else if (shortage > 0) {
        node.textContent = 'Insufficient GFun. Need ' + shortage + ' more.';
        node.dataset.status = 'insufficient';
      } else {
        node.textContent = 'GFun ready';
        node.dataset.status = 'ready';
      }
    });

    forEachNode(document.querySelectorAll(REMAINING_PREVIEW_SELECTOR), function (node) {
      var show = !unavailable && required > 0 && shortage === 0;
      setHidden(node, !show);
      node.dataset.status = unavailable ? 'unavailable' : shortage > 0 ? 'insufficient' : 'ready';
    });

    forEachNode(document.querySelectorAll(INSUFFICIENT_NOTICE_SELECTOR), function (node) {
      var showNotice = !unavailable && required > 0 && shortage > 0;
      setHidden(node, !showNotice);
      node.dataset.status = showNotice ? 'insufficient' : 'idle';
    });
  }

  function setDocumentBalanceState(state, source, reason) {
    document.documentElement.setAttribute('data-gfun-balance-state', state);
    document.documentElement.setAttribute('data-gfun-balance-source', source || '');
    if (reason) document.documentElement.setAttribute('data-gfun-balance-reason', reason);
    else document.documentElement.removeAttribute('data-gfun-balance-reason');
  }

  function renderUnavailableBalance(reason) {
    hasKnownBalance = false;
    currentBalance = 0;
    currentBalanceSource = '';
    balanceCustomerId = '';
    syncRuntimeBalance(null);
    var authenticated = Boolean(getCustomerId());

    forEachNode(document.querySelectorAll(BALANCE_SELECTOR), function (node) {
      node.textContent = '—';
      node.classList.remove('balance-loaded');

      if (node.dataset) {
        node.dataset.gfunBalance = '';
        node.dataset.balance = '';
        node.dataset.gfunAuthenticated = authenticated ? 'true' : 'false';
        node.dataset.gfunBalanceState = 'unavailable';
      }

      node.setAttribute(
        'aria-label',
        authenticated ? 'GFun balance unavailable' : 'Sign in to check GFun balance'
      );
    });

    updateBalanceCheckerUI(0, true);
    setDocumentBalanceState('unavailable', '', reason || 'BALANCE_UNAVAILABLE');
    dispatchRuntimeEvent('gfun:unavailable', {
      reason: reason || 'BALANCE_UNAVAILABLE',
      authenticated: authenticated,
    });
  }

  function renderBalance(balance, source) {
    var value = parseBalance(balance);
    if (value === null) {
      renderUnavailableBalance('INVALID_BALANCE_VALUE');
      return null;
    }

    if (!ensureCustomer()) {
      renderUnavailableBalance('CUSTOMER_NOT_AUTHENTICATED');
      return null;
    }
    balanceCustomerId = ensureCustomer();
    currentBalance = value;
    hasKnownBalance = true;
    currentBalanceSource = source || 'unknown';

    forEachNode(document.querySelectorAll(BALANCE_SELECTOR), function (node) {
      node.textContent = String(value);
      node.classList.add('balance-loaded');

      if (node.dataset) {
        node.dataset.gfunBalance = String(value);
        node.dataset.balance = String(value);
        node.dataset.gfunAuthenticated = 'true';
        node.dataset.gfunBalanceState = 'ready';
        node.dataset.gfunBalanceSource = currentBalanceSource;
      }

      node.setAttribute('aria-label', 'GFun balance: ' + value);
    });

    updateBalanceCheckerUI(value, false);
    setDocumentBalanceState('ready', currentBalanceSource, '');
    return value;
  }

  function dispatchBalanceUpdated(balance, source) {
    var value = parseBalance(balance);
    if (value === null) return;

    var detail = {
      ok: true,
      type: 'gfun.balance',
      result_surface: 'panel',
      balance: value,
      data: { balance: value },
      customer_id: balanceCustomerId,
      meta: {
        customer_id: balanceCustomerId,
        source: source || 'gfun-manager.js',
        api_family: 'gcity-core-api',
        canonical_endpoint: '/api/gfun/balance',
      },
    };

    publishedBalanceDetail = detail;
    dispatchRuntimeEvent('gfun:updated', detail);
    dispatchRuntimeEvent('gfun:balance-updated', detail);
  }

  function markInsufficient(element, required, balance) {
    var shortage = Math.max(0, required - balance);
    if (!element) return;

    if (element.dataset) {
      element.dataset.gfunState = 'insufficient';
      element.dataset.gfunShortage = String(shortage);
      element.dataset.gfunReason = 'INSUFFICIENT_GFUN';
    }

    element.setAttribute('aria-disabled', 'true');
    updateBalanceCheckerUI(balance, false);

    dispatchRuntimeEvent('gfun:insufficient', {
      ok: false,
      type: 'gfun.gate',
      result_surface:
        element.dataset && element.dataset.resultSurface ? element.dataset.resultSurface : 'panel',
      data: { required: required, balance: balance, shortage: shortage },
      meta: {
        reason: 'INSUFFICIENT_GFUN',
        action: element.dataset ? element.dataset.gfunAction || element.dataset.action || '' : '',
        trigger: element,
      },
    });
  }

  function markReady(element, required, balance) {
    if (!element) return;

    if (element.dataset) {
      element.dataset.gfunState = 'ready';
      element.dataset.gfunShortage = '0';
      delete element.dataset.gfunReason;
    }

    element.setAttribute('aria-disabled', 'false');
    updateBalanceCheckerUI(balance, false);

    dispatchRuntimeEvent('gfun:ready', {
      ok: true,
      type: 'gfun.gate',
      result_surface:
        element.dataset && element.dataset.resultSurface ? element.dataset.resultSurface : 'panel',
      data: { required: required, balance: balance, shortage: 0 },
      meta: {
        action: element.dataset ? element.dataset.gfunAction || element.dataset.action || '' : '',
        trigger: element,
      },
    });
  }

  function markUnavailable(element, reason) {
    if (!element) return;

    if (element.dataset) {
      element.dataset.gfunState = 'unavailable';
      element.dataset.gfunShortage = '';
      element.dataset.gfunReason = reason || 'BALANCE_UNAVAILABLE';
    }

    element.setAttribute('aria-disabled', 'true');
    updateBalanceCheckerUI(0, true);
  }

  function evaluateGate(element) {
    if (!element || !isActionable(element)) return true;

    var required = getRequiredCost(element);
    if (!required || required <= 0) {
      markReady(element, required, hasKnownBalance ? currentBalance : 0);
      return true;
    }

    if (!getCustomerId()) {
      markUnavailable(element, 'CUSTOMER_NOT_AUTHENTICATED');
      return false;
    }

    if (!hasKnownBalance) {
      markUnavailable(element, 'BALANCE_NOT_VERIFIED');
      return false;
    }

    if (currentBalance < required) {
      markInsufficient(element, required, currentBalance);
      return false;
    }

    markReady(element, required, currentBalance);
    return true;
  }

  function bindGateElement(element) {
    if (!element || element.getAttribute(BOUND_ATTR) === 'true') return;
    if (!isActionable(element)) return;

    element.setAttribute(BOUND_ATTR, 'true');
    evaluateGate(element);

    element.addEventListener(
      'click',
      function (event) {
        if (evaluateGate(element)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  function hydrateActionCosts(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return;

    var contractNodes = scope.matches && scope.matches(COST_SOURCE_SELECTOR) ? [scope] : [];
    contractNodes = contractNodes.concat(
      Array.prototype.slice.call(scope.querySelectorAll(COST_SOURCE_SELECTOR))
    );

    contractNodes.forEach(function (owner) {
      if (!owner || !owner.dataset || isActionable(owner)) return;

      var cost =
        owner.dataset.gfunContractCost ||
        owner.dataset.gfunCost ||
        owner.dataset.gfunRequired ||
        owner.dataset.gfunConsumePerCall ||
        owner.dataset.gfunConsume ||
        '';
      var normalizedCost = normalizeCost(cost);
      if (cost === '' || normalizedCost <= 0) return;

      var targets = owner.querySelectorAll ? owner.querySelectorAll(HYDRATE_TARGET_SELECTOR) : [];

      forEachNode(targets, function (target) {
        if (!target || !target.dataset) return;
        if (target.dataset.gfunInheritCost === 'false') return;
        if (target.closest('[data-free-zone="true"]')) return;

        if (
          !target.dataset.gfunCost &&
          !target.dataset.gfunRequired &&
          !target.dataset.gfunConsumePerCall &&
          !target.dataset.gfunContractCost
        ) {
          target.dataset.gfunCost = String(normalizedCost);
        }

        if (!target.dataset.gfunAction) {
          target.dataset.gfunAction = target.dataset.action || 'execute';
        }

        if (!target.dataset.resultSurface && owner.dataset.resultSurface) {
          target.dataset.resultSurface = owner.dataset.resultSurface;
        }
        if (!target.dataset.canonicalEndpoint && owner.dataset.canonicalEndpoint) {
          target.dataset.canonicalEndpoint = owner.dataset.canonicalEndpoint;
        }
        if (!target.dataset.apiFamily && owner.dataset.apiFamily) {
          target.dataset.apiFamily = owner.dataset.apiFamily;
        }
      });
    });
  }

  function bindGates(root) {
    var scope = root || document;
    hydrateActionCosts(scope);

    var actionNodes = scope.querySelectorAll ? scope.querySelectorAll(ACTION_SELECTOR) : [];
    forEachNode(actionNodes, bindGateElement);

    if (scope.matches && scope.matches(ACTION_SELECTOR)) bindGateElement(scope);
  }

  function evaluateAllGates() {
    forEachNode(document.querySelectorAll(ACTION_SELECTOR), evaluateGate);
  }

  function extractBalance(result, customerId) {
    var payload = result && typeof result === 'object' ? result : {};
    if (
      payload.ok === false ||
      payload.success === false ||
      (payload.ok !== true && payload.success !== true)
    )
      throw new Error('GFUN_BALANCE_RESPONSE_NOT_OK');

    var data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
    var owner = data.customer_id || payload.customer_id;
    if (String(owner || '') !== String(customerId || ''))
      throw new Error('GFUN_BALANCE_OWNER_MISMATCH');
    var keys = ['balance', 'gfun_balance', 'currentBalance'];

    for (var index = 0; index < keys.length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(data, keys[index])) continue;
      var value = parseBalance(data[keys[index]]);
      if (value !== null) return value;
    }

    throw new Error('GFUN_BALANCE_MISSING');
  }

  async function fetchRemoteBalance(force) {
    var customerId = getCustomerId();
    var executor = getExecutor();

    if (!customerId) throw new Error('CUSTOMER_NOT_AUTHENTICATED');
    if (!executor) throw new Error('GFUN_EXECUTOR_UNAVAILABLE');

    var ctx = {
      delivery_type: 'credit',
      delivery_method: 'instant',
      engine_type: 'ledger_balance',
      default_scene: 'gfun-dashboard',
      api_family: 'gcity-core-api',
      canonical_endpoint: '/api/gfun/balance',
      api_trigger_enabled: true,
      result_surface: 'panel',
      requires_entitlement: false,
      requires_gfun: false,
      data: {
        customerId: customerId,
        force: force === true ? '1' : '',
      },
    };

    var result = await executor.execute(ctx, { skipGate: true });
    return extractBalance(result, customerId);
  }

  function syncRuntimeBalance(balance) {
    var value = balance === null ? null : parseBalance(balance);

    if (window.GCITY && window.GCITY.customer) {
      window.GCITY.customer.gfunBalance = value;
      window.GCITY.customer.gfun_balance = value;
    }
    if (window.CUSTOMER_DATA && typeof window.CUSTOMER_DATA === 'object') {
      window.CUSTOMER_DATA.gfunBalance = value;
      window.CUSTOMER_DATA.gfun_balance = value;
    }

    if (window.LITTLEG_SERVER_DATA && typeof window.LITTLEG_SERVER_DATA === 'object') {
      window.LITTLEG_SERVER_DATA.gfunBalance = value;
    }

    if (window.gameState && typeof window.gameState === 'object') {
      window.gameState.wallet = window.gameState.wallet || {};
      window.gameState.wallet.gfun = value;
    }

    return value;
  }

  function applyBalance(balance, source, _persist) {
    var value = parseBalance(balance);
    if (value === null) {
      renderUnavailableBalance('INVALID_BALANCE_VALUE');
      evaluateAllGates();
      return null;
    }

    syncRuntimeBalance(value);
    renderBalance(value, source || 'gfun-manager.js');
    evaluateAllGates();
    dispatchBalanceUpdated(value, source || 'gfun-manager.js');
    return value;
  }

  async function refreshBalance(force) {
    var customerId = ensureCustomer();
    if (!customerId) {
      renderUnavailableBalance('CUSTOMER_NOT_AUTHENTICATED');
      evaluateAllGates();
      return null;
    }
    if (refreshPromise) return refreshPromise;
    if (force !== true && serverSeedAllowed) {
      var metafieldBalance = readShopifyMetafieldBalance();
      if (metafieldBalance !== null)
        return applyBalance(metafieldBalance, 'shopify-metafield', false);
      var snapshot = getBalanceSnapshot();
      if (snapshot.known) return snapshot.balance;
    }
    serverSeedAllowed = false;
    var generation = balanceGeneration;
    renderUnavailableBalance('BALANCE_REFRESH_PENDING');
    evaluateAllGates();
    var pending = fetchRemoteBalance(force)
      .then(function (balance) {
        ensureCustomer();
        if (generation !== balanceGeneration || customerId !== customerKey()) return null;
        return applyBalance(balance, 'gcity-core-api', false);
      })
      .catch(function (error) {
        ensureCustomer();
        if (generation !== balanceGeneration || customerId !== customerKey()) return null;
        var reason = error && error.message ? error.message : 'BALANCE_FETCH_FAILED';
        renderUnavailableBalance(reason);
        evaluateAllGates();
        dispatchRuntimeEvent('gfun:error', {
          ok: false,
          type: 'gfun.balance',
          data: {},
          meta: { reason: reason },
        });
        return null;
      })
      .finally(function () {
        if (refreshPromise === pending) refreshPromise = null;
      });
    refreshPromise = pending;
    return pending;
  }

  function init() {
    if (initialized) return;
    initialized = true;

    bindGates(document);

    if (!ensureCustomer()) {
      renderUnavailableBalance('CUSTOMER_NOT_AUTHENTICATED');
      evaluateAllGates();
      return;
    }

    var seededBalance = seedBalanceFromDOM();
    if (!Number.isNaN(seededBalance)) {
      applyBalance(seededBalance, 'server-rendered-dom', true);
    } else {
      renderUnavailableBalance('BALANCE_REFRESH_PENDING');
      evaluateAllGates();
    }

    refreshBalance(false);
  }

  window.GFunManager = {
    __installed: true,
    version: VERSION,
    refreshBalance: refreshBalance,
    fetchLatestBalance: refreshBalance,
    renderBalance: function () {
      return refreshBalance(true);
    },
    getBalanceSnapshot: getBalanceSnapshot,
    renderUnavailableBalance: renderUnavailableBalance,
    readLocalBalance: readLocalBalance,
    readFreshLocalBalance: readFreshLocalBalance,
    seedBalanceFromDOM: seedBalanceFromDOM,
    bindGates: bindGates,
    hydrateActionCosts: hydrateActionCosts,
    evaluateGate: evaluateGate,
    evaluateAllGates: evaluateAllGates,
    getRequiredCost: getRequiredCost,
    normalizeCost: normalizeCost,
    init: init,
    get currentBalance() {
      return getBalanceSnapshot().balance;
    },
    get hasKnownBalance() {
      return getBalanceSnapshot().known;
    },
    get currentBalanceSource() {
      return getBalanceSnapshot().source;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  ['gfun:balance:update', 'gfun:updated', 'gfun:balance-updated'].forEach(function (name) {
    window.addEventListener(name, function (event) {
      if (event.detail === publishedBalanceDetail) return;
      syncRuntimeBalance(getBalanceSnapshot().balance);
      refreshBalance(true);
    });
  });

  document.addEventListener('gcity:auth:ready', function () {
    ensureCustomer();
    refreshBalance(true);
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshBalance(true);
  });

  window.addEventListener('gcity:delivery:updated', function (event) {
    var detail = event && event.detail ? event.detail : {};

    if (
      detail.action === 'gfun.verify' ||
      detail.type === 'gfun.topup' ||
      detail.type === 'gfun.balance' ||
      detail.type === 'gfun.delivery' ||
      detail.result_surface === 'dashboard'
    ) {
      refreshBalance(true);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    var target = event && event.target ? event.target : document;
    bindGates(target);
    evaluateAllGates();
  });
})(window, document);
